import { useMemo } from 'react'
import { computeWordDiff } from '../lib/diffEngine'
import type { DiffToken } from '../types'
import { cn } from '@/shared/lib/utils'

interface DiffViewProps {
  original: string
  redacted: string
}

function DiffSide({
  tokens,
  showType,
  label,
}: {
  tokens: DiffToken[]
  showType: 'removed' | 'added'
  label: string
}) {
  return (
    <div className="flex flex-1 flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="min-h-[80px] rounded-md border bg-muted/30 p-3 text-sm leading-relaxed">
        {tokens.map((token, i) => {
          if (token.removed && showType === 'removed') {
            return (
              <del
                key={i}
                className="rounded bg-red-100 text-red-700 no-underline line-through dark:bg-red-950/40 dark:text-red-400"
              >
                {token.value}
              </del>
            )
          }
          if (token.added && showType === 'added') {
            return (
              <ins
                key={i}
                className="rounded bg-green-100 text-green-700 underline dark:bg-green-950/40 dark:text-green-400"
                style={{ textDecorationStyle: 'solid' }}
              >
                {token.value}
              </ins>
            )
          }
          // Skip tokens that belong to the other side
          if (token.added || token.removed) return null
          return <span key={i}>{token.value}</span>
        })}
      </div>
    </div>
  )
}

export function DiffView({ original, redacted }: DiffViewProps) {
  const tokens = useMemo(() => computeWordDiff(original, redacted), [original, redacted])

  const hasChanges = tokens.some((t) => t.added || t.removed)

  return (
    <div className="flex flex-col gap-3">
      <div className={cn('flex gap-4', 'flex-col md:flex-row')}>
        <DiffSide tokens={tokens} showType="removed" label="Original" />
        <DiffSide tokens={tokens} showType="added" label="Redacted" />
      </div>
      {!hasChanges && (
        <p className="text-center text-xs text-muted-foreground italic">
          No changes — the message was already excellent.
        </p>
      )}
    </div>
  )
}
